import React, { createContext, useState, useEffect, useContext } from 'react';
import api, { setAuthToken } from '../services/api';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    const token = localStorage.getItem('token');
    if (token) {
      setAuthToken(token);
      try {
        const response = await api.get('/auth/profile');
        setUser(response.data.user);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        setAuthToken(null);
      }
    }
    setLoading(false);
  }

  async function login(email, password, rememberMe = false) {
    try {
      setError(null);
      const response = await api.post('/auth/login', {
        email,
        password,
        rememberMe
      });

      const { token, user } = response.data;

      localStorage.setItem('token', token);
      setAuthToken(token);
      setUser(user);

      return user;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      setAuthToken(null);
      setUser(null);
    }
  }

  async function updatePassword(currentPassword, newPassword) {
    try {
      setError(null);
      const response = await api.put('/auth/password', {
        currentPassword,
        newPassword
      });

      // Update token if a new one is provided (server invalidates old tokens on password change)
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        setAuthToken(response.data.token);
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  }

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    updatePassword,
    checkAuth
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
